/**
 * Store pour la gestion des packs de crédits
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useCreditPackStore = defineStore('creditPacks', (() => {

    // State de la liste des credits (total, packs)
    const creditPackList = ref({});

    // State pour différencier les données de la liste des packs
    const creditPacks = ref([]);

    /**
     * Retourne la liste des packs
     * @return array
     */
    const getCreditPackList = computed(() => creditPackList.value || {});

    /**
     * Retourne le pack dont l'identifiant est en paramètre
     * @param int packId
     * @return object|null
     */
    const getCreditPackById = computed(() => {
        return ((packId) => {
            const index = creditPacks.value.findIndex((creditPackFromState) => creditPackFromState.id === packId);
            if(index === -1) {
                return null;
            }
            return creditPacks.value[index];
        });
    });

    /**
     * Retourne les packs du type en paramètre
     * @param int type
     * @return array
     */
    const getCreditPackByType = computed(() => {
        return ((type) => {
            const items = getCreditPackList.value.packs || [];

            return items.filter((packFromState) => {
                return (packFromState.type === type);
            });
        });
    });

    /**
     * Chargement de la liste des packs de crédits
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadCreditPackList = ((params) => {

        const path = '/api/credit-packs';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetCreditPackList();
                }

                updateCreditPackList(response.data.data);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Chargement des secteurs ciblés par le pack de crédit
     * @param object pack
     * @return Promise
     */
    const loadVisibilitySectors = ((pack) => {

        const path = '/api/admin/credit-packs/:packId/visibility/sectors'.replace(':packId', pack.id);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.pack) {
                updateCreditPack(response.data.pack);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });
    });

    /**
     * Chargement des utilisateurs ciblés par le pack de crédit
     * @param object pack
     * @param object params Paramètres de recherche (offset, limit)
     * @return Promise
     */
    const loadVisibilityUsers = ((pack, params = {}) => {

        const path = '/api/admin/credit-packs/:packId/visibility/users'.replace(':packId', pack.id);

        const offset = params.offset || 0;
        const limit = params.limit || 20;

        return axios.get(path, {
            params: {
                offset: offset,
                limit: limit
            }
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                updateCreditPackVisibilityUsers(pack, response.data.data);
                return Promise.resolve();
            } else {

                return Promise.reject();
            }
        });
    });

    /**
     * Suppression d'un secteur du pack de crédit en paramètre
     * @param object pack
     * @param object sector
     * @return void
     */
    const removeSectorFromCreditPackVisibility = ((pack, sector) => {
        const path = '/api/admin/credit-packs/:packId/visibility/sectors/:sectorId'
            .replace(':packId', pack.id)
            .replace(':sectorId', sector.id)
        ;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                onRemoveSectorFromCreditPackVisibility(pack, sector);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });
    });

    /**
     * Met à jour la visibilité des utilisateurs du pack dans le store après la suppression d'un secteur
     * @param object pack
     * @param object sector
     * @return void
     */
    const onRemoveSectorFromCreditPackVisibility = ((pack, sector) => {
        const currentPacks = creditPacks.value;
        const packIndex = currentPacks.findIndex((packFromState) => packFromState.id === pack.id);
        if(packIndex === -1) {
            return;
        }

        const currentPack = currentPacks[packIndex];
        const visibility = currentPack.visibility || {};
        const sectorsVisibility = currentPack.visibility.sectors || [];

        const indexSector = sectorsVisibility.findIndex((currentSector) => currentSector.id === sector.id);
        if(indexSector === -1) {
            return;
        }
        sectorsVisibility.splice(indexSector, 1);

        visibility.sectors = sectorsVisibility;
        currentPacks[packIndex].visibility = { ...visibility };
    });

    /**
     * Suppression d'un utilisateur du pack de crédit en paramètre
     * @param object pack
     * @param object user
     * @return void
     */
    const removeUserFromCreditPackVisibility = ((pack, user) => {
        const path = '/api/admin/credit-packs/:packId/visibility/users/:userId'
            .replace(':packId', pack.id)
            .replace(':userId', user.id)
        ;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                onRemoveUserFromCreditPackVisibility(pack, user);
                return Promise.resolve();
            } else {

                return Promise.reject();
            }
        });
    });

    /**
     * Met à jour la visibilité des utilisateurs du pack dans le store après la suppression d'un utilisateur
     * @param object pack
     * @param object user
     * @return void
     */
    const onRemoveUserFromCreditPackVisibility = ((pack, user) => {
        const currentPacks = creditPacks.value;
        const packIndex = currentPacks.findIndex((packFromState) => packFromState.id === pack.id);
        if(packIndex === -1) {
            return;
        }

        const currentPack = currentPacks[packIndex];
        const visibility = currentPack.visibility || {};
        const usersVisibility = currentPack.visibility.users || {};

        if(usersVisibility.total === undefined) {
            return;
        }

        const indexUser = usersVisibility.users.findIndex((currentUser) => currentUser.id === user.id);
        if(indexUser === -1) {
            return;
        }

        usersVisibility.total -= 1;
        usersVisibility.users.splice(indexUser, 1);

        visibility.users = usersVisibility;
        currentPacks[packIndex].visibility = { ...visibility };
    });

    /**
     * Mise à jour de la liste des utilisateurs ciblé par le pack en paramètre
     * @param object pack
     * @param object data Données de la liste : total et users ; dans users sont les utilisateurs à fusionner
     * @return void
     */
    const updateCreditPackVisibilityUsers = ((pack, data) => {

        const currentPacks = creditPacks.value;
        const index = currentPacks.findIndex((packFromState) => packFromState.id === pack.id);
        if(index === -1) {
            return;
        }

        const currentPack = currentPacks[index];
        const visibility = currentPack.visibility || {};
        const usersVisibility = currentPack.visibility.users || {};

        usersVisibility.total = data.total;
        usersVisibility.users = usersVisibility.users || []
        const currentUsers = usersVisibility.users;

        const newUsers = data.items.filter((item) => ! currentUsers.find((current) => current.id === item.id));
        usersVisibility.users = [ ...currentUsers, ...newUsers ];

        visibility.users = usersVisibility;
        currentPacks[index].visibility = { ...visibility };

        updateCreditPack(currentPacks[index]);
    });

    /**
     * Mise à jour de la liste des secteurs ciblés par le pack en paramètre
     * @param object pack
     * @param array sectors
     * @return void
     */
    const updateCreditPackVisibilitySectors = ((pack, sectors) => {

        const currentPacks = creditPacks.value;
        const index = currentPacks.findIndex((packFromState) => packFromState.id === pack.id);
        if(index === -1) {
            return;
        }

        const currentPack = currentPacks[index];
        const visibility = currentPack.visibility || {};
        const sectorsVisibility = currentPack.visibility.sectors || [];


        const newSectors = sectors.filter((item) => ! sectorsVisibility.find((current) => current.id === item.id));

        visibility.sectors =  [ ...sectorsVisibility, ...newSectors ];
        currentPacks[index].visibility = { ...visibility };

        updateCreditPack(currentPacks[index]);
    });

    /**
     * Mise à jour des données d'un pack déjà chargé
     * @param object pack Données du pack à mettre à jour dans la liste
     * @return Promise
     */
    const updateCreditPack = ((pack) => {

        const currentPacks = creditPacks.value;

        const index = currentPacks.findIndex((packFromState) => packFromState.id === pack.id);
        if(index !== -1) {
            creditPacks.value[index] = { ...creditPacks.value[index], ...pack };
        } else {
            creditPacks.value.push(pack);
        }

        // Met à jour le pack dans la liste
        const currentList = creditPackList.value.packs;
        if(currentList !== undefined) {
            const index = currentList.findIndex((packFromList) => packFromList.id === pack.id);
            if(index !== -1) {
                const oldPack = creditPackList.value.packs[index];
                creditPackList.value.packs[index] = { ...oldPack, ...pack };
            }
        }

    });

    /**
     * Modifie la liste des packs
     * @param data Données des packs : total et items
     * @return void
     */
    const updateCreditPackList = ((data) => {

        const currentData = creditPackList.value;
        if(currentData.total === undefined) {
            creditPackList.value = {
                total: data.total,
                packs: data.items
            };

            data.items.forEach((pack) => {
                updateCreditPack(pack);
            });

        } else {
            creditPackList.value.total = data.total;

            const currentPacks = creditPackList.value.packs;
            const newPacks = data.items.filter((item) => ! currentPacks.find((current) => current.id === item.id));
            creditPackList.value.packs = [ ...currentPacks, ...newPacks ];

            newPacks.forEach((pack) => {
                updateCreditPack(pack);
            });
        }

    });

    /**
     * Réinitialisation de la liste packs de crédit
     * @return void
     */
    const resetCreditPackList = (() => {
        creditPackList.value = {};
    });

    return {
        getCreditPackList,
        getCreditPackById,
        getCreditPackByType,
        loadCreditPackList,
        updateCreditPack,
        loadVisibilitySectors,
        loadVisibilityUsers,
        removeUserFromCreditPackVisibility,
        removeSectorFromCreditPackVisibility,
        updateCreditPackVisibilitySectors,
        updateCreditPackVisibilityUsers
    };
}));
